"use client";

import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetTicketByIdQuery,
  useReplyToTicketUserMutation,
} from "@/features/api/supportApi";

import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

import { Loader2, Send, Bell } from "lucide-react";
import { toast } from "sonner";

/* ================= UNREAD CHECK (USER) ================= */
const hasUnreadAdminReply = (ticket) => {
  if (!ticket || !Array.isArray(ticket.replies)) return false;

  const last = ticket.replies[ticket.replies.length - 1];
  if (!last || last.repliedBy !== "admin") return false;

  const lastSeen = localStorage.getItem(
    `support:lastSeenUser:${ticket._id}`
  );

  if (!lastSeen) return true;

  return (
    new Date(last.repliedAt).getTime() >
    new Date(lastSeen).getTime()
  );
};

const UserTicketChat = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const bottomRef = useRef(null);
  const prevMessageCount = useRef(0);
  const [message, setMessage] = useState("");

  /* ================= FETCH (AUTO REFRESH) ================= */
  const {
    data: ticket,
    isLoading,
    isError,
    refetch,
  } = useGetTicketByIdQuery(id, {
    skip: !id,
    pollingInterval: 5000,        // ✅ refresh every 5 sec
    refetchOnFocus: true,         // ✅ refetch when tab focused
    refetchOnReconnect: true,     // ✅ refetch on network reconnect
  });

  const [sendReply, { isLoading: sending }] =
    useReplyToTicketUserMutation();

  /* ================= MARK READ ================= */
  useEffect(() => {
    if (ticket?._id) {
      localStorage.setItem(
        `support:lastSeenUser:${ticket._id}`,
        new Date().toISOString()
      );
    }
  }, [ticket?._id]);

  /* ================= MESSAGES ================= */
  const messages = useMemo(() => {
    if (!ticket) return [];

    const initial = {
      id: "initial",
      from: "user",
      text: String(ticket.message || ""),
      time: ticket.createdAt || new Date(),
    };

    const replies = Array.isArray(ticket.replies)
      ? ticket.replies.map((r, i) => ({
          id: r._id || `${r.repliedAt}-${i}`,
          from: r.repliedBy === "admin" ? "admin" : "user",
          text: String(r.message || ""),
          time: r.repliedAt || new Date(),
        }))
      : [];

    return [initial, ...replies];
  }, [ticket]);

  /* ================= AUTO SCROLL (ONLY ON NEW MSG) ================= */
  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      prevMessageCount.current = messages.length;
    }
  }, [messages.length]);

  /* ================= SEND ================= */
  const handleSend = async () => {
    if (!message.trim()) {
      toast.error("Message cannot be empty");
      return;
    }

    try {
      await sendReply({
        id,
        message: message.trim(),
      }).unwrap();

      setMessage("");
      toast.success("Message sent");

      // ✅ instantly refresh after user sends message
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to send message");
    }
  };

  /* ================= STATES ================= */
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (isError || !ticket) {
    return (
      <div className="text-center text-red-500 py-20">
        Ticket not found or access denied
      </div>
    );
  }

  const unread = hasUnreadAdminReply(ticket);

  /* ================= UI ================= */
  return (
    <div className="max-w-4xl mx-auto p-4 h-[85vh] flex flex-col">
      {/* HEADER */}
      <Card className="mb-4">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="text-lg">
              {ticket.subject || "Support Ticket"}
            </CardTitle>

            {unread && (
              <span className="flex items-center gap-1 text-xs bg-red-500 text-white px-2 py-1 rounded-full animate-pulse">
                <Bell size={12} /> New Reply
              </span>
            )}
          </div>

          <Button
            variant="outline"
            onClick={() => navigate("/dashboard/support")}
          >
            ← Back
          </Button>
        </CardHeader>
      </Card>

      {/* CHAT */}
      <div className="flex-1 overflow-y-auto bg-[#e5ddd5] rounded-lg p-4 space-y-4">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${
              msg.from === "user"
                ? "justify-end"
                : "justify-start"
            }`}
          >
            <div
              className={`max-w-[70%] px-4 py-2 rounded-xl text-sm shadow ${
                msg.from === "user"
                  ? "bg-[#dcf8c6] text-gray-900 rounded-br-none"
                  : "bg-white text-gray-800 rounded-bl-none"
              }`}
            >
              <p className="whitespace-pre-wrap">
                {msg.text}
              </p>
              <span className="block text-[10px] opacity-60 mt-1 text-right">
                {new Date(msg.time).toLocaleString()}
              </span>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* INPUT */}
      {ticket.status !== "solved" ? (
        <div className="mt-4 flex gap-2">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message..."
            rows={3}
            className="resize-none"
          />
          <Button
            onClick={handleSend}
            disabled={sending}
            className="px-6"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send size={18} />
            )}
          </Button>
        </div>
      ) : (
        <div className="mt-4 text-center text-gray-600">
          This ticket has been marked as solved. Chat is closed.
        </div>
      )}
    </div>
  );
};

export default UserTicketChat;
